/**
 * Étape 3 du parcours d'ajout (ou édition depuis la grille) :
 * formulaire de paramétrage avec aperçu live, puis ajout ou mise à jour du widget.
 * Route : /widgets/:widgetType/configure
 */
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { fetchBuildings } from "../../../api/building";
import { fetchAccessibleDevices } from "../../../api/device";
import { isRegulatorType } from "../../../api/regulator";
import { useRegulatorMeasures } from "./widgets/useRegulatorMeasures";
import { missingRight, regulatorRights } from "./regulatorRights";
import { GeoResult } from "../../../api/weather";
import {
  Building,
  DeviceWithoutLegacyPassword,
} from "../../../generated/types";
import { Button } from "../../common/Button";
import { Header } from "../../Header";
import { sectionToIcon } from "../../type/default";
import { widgetSelectClassName } from "./AddWidgetLayout";
import { CitySearchField } from "./CitySearchField";
import { ResizableWidgetPreview } from "./ResizableWidgetPreview";
import {
  ACCESS_RIGHT_LABELS,
  REGULATOR_METRIC_OPTIONS,
  METRIC_COLORS,
  buildDefaultWidgetProps,
  buildWidgetPropsFromConfig,
  findSizeByDimensions,
  getWidgetCatalogEntry,
  getWidgetSizeOption,
  GROUP_LABELS,
  isMetricSupported,
  metricAccessRight,
  WidgetConfigField,
} from "./widgetCatalog";
import {
  clearWidgetDraft,
  getWidgetDraft,
  setPendingWidget,
  setPendingWidgetUpdate,
  setWidgetDraft,
} from "./widgetDraft";

function getInitialConfig(
  entry: ReturnType<typeof getWidgetCatalogEntry>,
  draftConfig: Record<string, unknown>
) {
  if (!entry) return {};

  const config = buildDefaultWidgetProps(entry);

  entry.configFields.forEach((field) => {
    if (
      field.defaultValue !== undefined &&
      draftConfig[field.key] === undefined
    ) {
      config[field.key] = field.defaultValue;
    }
  });

  // Le brouillon prime (inclut les références comme buildingId et le libellé name).
  // Une clé PRÉSENTE dans le brouillon s'applique même à `undefined` : sans ça, une vedette
  // retirée à la punaise réapparaîtrait au retour dans le configurateur (défaut du catalogue).
  Object.keys(draftConfig).forEach((key) => {
    config[key] = draftConfig[key];
  });

  return config;
}

/**
 * Tuile à icône (case à cocher d'indicateur) : surlignée en olive quand active.
 *
 * Quand `onToggleHighlight` est fourni, une punaise apparaît à droite des tuiles COCHÉES : il met
 * la grandeur en avant dans le widget (grande tuile). L'enveloppe est un `<div>` et non un
 * `<button>` — imbriquer un bouton dans un bouton est invalide en HTML et casse le clic.
 */
function IndicatorTile({
  label,
  icon,
  active,
  disabled = false,
  lockedReason,
  highlighted = false,
  highlightDisabled = false,
  color,
  onToggle,
  onToggleHighlight,
}: {
  label: string;
  icon?: IconProp;
  active: boolean;
  /** Vrai quand la case n'est pas cochable (limite atteinte ou droit manquant). */
  disabled?: boolean;
  /**
   * Renseigné quand le blocage vient d'un DROIT manquant (et non de la limite de 8) : la
   * tuile porte alors un cadenas et l'infobulle nomme le droit. Distinguer les deux évite de
   * chercher une limite inexistante.
   */
  lockedReason?: string;
  /** Vrai si cette grandeur est l'une des vedettes. */
  highlighted?: boolean;
  /** Vrai quand les 2 vedettes sont déjà prises et que celle-ci n'en est pas une. */
  highlightDisabled?: boolean;
  /** Teinte de l'icône (couleur de la grandeur), pour que la sélection corresponde au widget. */
  color?: string;
  onToggle: () => void;
  onToggleHighlight?: () => void;
}) {
  return (
    <div
      // Accessibilité : `text-sm` (et non `text-xs`) et gris conformes AA. L'état désactivé
      // gardait `text-grey-light` (1.25:1) : une tuile verrouillée doit rester LISIBLE — sinon
      // l'utilisateur ne sait même pas quelle grandeur lui est refusée. `grey-medium` la
      // distingue toujours d'une tuile active sans la rendre invisible.
      className={`flex items-center gap-2 rounded-lg border pl-2.5 pr-1.5 py-2 text-left text-sm transition-all ${
        active
          ? "border-secondary bg-secondary/10 text-tertiary"
          : disabled
            ? "border-grey-light bg-grey-light/20 text-grey-medium"
            : "border-grey-readable bg-white text-grey-readable hover:border-tertiary hover:bg-grey-light/30"
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={active}
        disabled={disabled}
        title={
          lockedReason ??
          (disabled
            ? "Limite atteinte : décochez une grandeur d'abord"
            : undefined)
        }
        className={`flex flex-1 min-w-0 items-center gap-2 text-left ${
          disabled ? "cursor-not-allowed" : ""
        }`}
      >
        {icon && (
          <FontAwesomeIcon
            icon={icon}
            className={`text-base shrink-0 ${
              active && !lockedReason ? "" : "text-grey-readable"
            }`}
            // Colorée seulement quand la grandeur est cochée ET accessible : une grille de
            // 20 tuiles toutes colorées deviendrait un arc-en-ciel illisible, et une tuile
            // verrouillée doit rester visuellement éteinte.
            style={{
              color: active && color && !lockedReason ? color : undefined,
            }}
          />
        )}
        <span className="flex-1 leading-tight font-oxaniumMedium truncate">
          {label}
        </span>
        {lockedReason && (
          <FontAwesomeIcon
            icon="lock"
            className="text-warning-readable text-xs shrink-0"
          />
        )}
        {active && !onToggleHighlight && !lockedReason && (
          <FontAwesomeIcon
            icon="check"
            className="text-tertiary text-xs shrink-0"
          />
        )}
      </button>

      {/* La punaise n'existe que sur une grandeur cochée : mettre en avant ce qui n'est pas
          affiché n'aurait aucun sens. */}
      {active && onToggleHighlight && (
        <button
          type="button"
          onClick={onToggleHighlight}
          aria-pressed={highlighted}
          aria-label={
            highlighted ? "Retirer de la mise en avant" : "Mettre en avant"
          }
          disabled={highlightDisabled}
          title={
            highlighted
              ? "Mise en avant — cliquez pour retirer"
              : highlightDisabled
                ? "2 grandeurs déjà mises en avant"
                : "Mettre en avant dans le widget"
          }
          // Punaise et pas œil : `eye`/`eye-slash` veut dire montrer/masquer partout ailleurs
          // (cf. PasswordField), on lisait donc « faire disparaître » au lieu de « mettre en avant ».
          // Actif = pastille PLEINE (fond olive) : `text-secondary` seul se confondait avec le
          // fond olive clair de la tuile cochée. Picto en `tertiary` et non `white` — blanc sur
          // olive ne donne que 2.23:1, contre 5.77:1 pour le vert foncé. Inactif = contour
          // visible sur fond blanc (l'ancien opacity-40 le rendait fantomatique).
          // 28px de côté : plancher de confort tactile/visuel, la pastille de 24 était juste.
          className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-full border transition-all ${
            highlighted
              ? "bg-secondary border-tertiary text-tertiary shadow-sm"
              : highlightDisabled
                ? "bg-white border-grey-light text-grey-medium cursor-not-allowed"
                : "bg-white border-grey-readable text-grey-readable hover:border-tertiary hover:text-tertiary hover:bg-secondary/20"
          }`}
        >
          <FontAwesomeIcon icon="thumbtack" className="text-sm" />
        </button>
      )}
    </div>
  );
}

function ConfigField({
  field,
  value,
  options,
  onChange,
}: {
  field: WidgetConfigField;
  value: unknown;
  options: { value: string; label: string }[];
  onChange: (value: unknown) => void;
}) {
  if (field.type === "checkbox") {
    return (
      <label className="flex items-center gap-3 text-tertiary cursor-pointer">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(event) => onChange(event.target.checked)}
          className="w-5 h-5 rounded border-grey-readable text-tertiary focus:ring-tertiary"
        />
        <span>{field.label}</span>
      </label>
    );
  }

  if (field.type === "citySearch") {
    return (
      <div>
        <label className="block text-tertiary mb-2">{field.label}</label>
        <CitySearchField
          value={value === undefined ? undefined : String(value)}
          onSelect={(geo) => onChange(geo)}
        />
      </div>
    );
  }

  if (field.type === "text") {
    return (
      <div>
        <label className="block text-tertiary mb-2">{field.label}</label>
        <input
          type="text"
          className={widgetSelectClassName}
          value={String(value ?? "")}
          placeholder={field.placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      </div>
    );
  }

  return (
    <div>
      <label className="block text-tertiary mb-2">{field.label}</label>
      <select
        className={widgetSelectClassName}
        value={String(value ?? "")}
        onChange={(event) => onChange(event.target.value)}
      >
        {value === undefined && (
          <option value="" disabled>
            Choisir…
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function AddWidgetConfigurePage() {
  const navigate = useNavigate();
  const { widgetType = "" } = useParams();
  const entry = getWidgetCatalogEntry(widgetType);
  const draft = getWidgetDraft();

  const size = useMemo(() => {
    if (!entry || !draft?.sizeId) return entry?.sizes[0];
    return getWidgetSizeOption(entry, draft.sizeId) ?? entry.sizes[0];
  }, [draft?.sizeId, entry]);

  const [config, setConfig] = useState<Record<string, unknown>>(() =>
    getInitialConfig(entry, draft?.config ?? {})
  );
  // Dimensions courantes (cellules) : reprises du widget édité, sinon de la taille
  // par défaut. Modifiables au resize, elles déterminent la taille à l'enregistrement.
  const [dimensions, setDimensions] = useState(() => ({
    w: draft?.w ?? size?.w ?? 4,
    h: draft?.h ?? size?.h ?? 4,
  }));
  const [buildings, setBuildings] = useState<Building[]>([]);
  // Régulateurs de l'utilisateur, pour résoudre `Building.device[]` (des id numériques) en
  // `uuid` — seule clé acceptée par la connexion à distance. Chargés ici pour figer l'uuid
  // dans la config du widget (choix du 2026-07-30 : pas d'appel API au clic).
  const [devices, setDevices] = useState<DeviceWithoutLegacyPassword[]>([]);
  const [devicesError, setDevicesError] = useState(false);
  const [devicesLoaded, setDevicesLoaded] = useState(false);
  const [devicesAttempt, setDevicesAttempt] = useState(0);
  const { t } = useTranslation();

  const hasBuildingField = Boolean(
    entry?.configFields.some((field) => field.dynamicOptions === "buildings")
  );
  // Le widget régulateur alimente son sélecteur avec `dynamicOptions: "devices"`.
  const hasDeviceField = Boolean(
    entry?.configFields.some((field) => field.dynamicOptions === "devices")
  );

  // Charge la liste des bâtiments pour alimenter le sélecteur (référence buildingId).
  useEffect(() => {
    if (!hasBuildingField) return;
    let active = true;
    fetchBuildings().then((res) => {
      if (active) setBuildings(res.Buildings);
    });
    return () => {
      active = false;
    };
  }, [hasBuildingField]);

  // Régulateurs ACCESSIBLES : tous pour un admin/psc, uniquement ceux liés à son compte
  // (LinkAccountDevice) pour les autres types — voir fetchAccessibleDevices.
  useEffect(() => {
    if (!hasDeviceField) return;
    let active = true;
    fetchAccessibleDevices().then((res) => {
      if (!active) return;
      setDevicesError(Boolean(res.error));
      setDevicesLoaded(true);
      // Seuls les régulateurs ont des mesures : un autre produit (alarme…) finirait en erreur.
      setDevices(res.devices.filter((device) => isRegulatorType(device.type)));
    });
    return () => {
      active = false;
    };
  }, [hasDeviceField, devicesAttempt]);

  // Droits bigdata du régulateur sélectionné. Ils appartiennent au couple (utilisateur,
  // BÂTIMENT) — c'est DATA qui résout le bâtiment du produit et joint les droits, dans la même
  // réponse que les mesures. Le configurateur n'a donc plus aucun appel dédié aux droits.
  const selectedDeviceUuid = config.deviceUuid as string | undefined;
  // Le type du produit fixe les grandeurs proposées (un legacy n'en fournit qu'une partie).
  const selectedDeviceType = devices.find(
    (device) => device.uuid === selectedDeviceUuid
  )?.type;
  const {
    granted,
    buildingId,
    loading: rightsLoading,
    error: rightsError,
  } = useRegulatorMeasures(selectedDeviceUuid);

  const rights = regulatorRights({
    deviceUuid: selectedDeviceUuid,
    loading: rightsLoading,
    error: rightsError,
    buildingId,
    granted,
  });
  const accessRights = rights.kind === "ok" ? rights : null;

  const rightsNotice = {
    forbidden: t("regulator-access-denied"),
    unavailable: t("regulator-rights-unavailable"),
    "no-building": t("regulator-no-building"),
    ok: "",
    unknown: "",
  }[rights.kind];

  // Purge des grandeurs devenues interdites : un widget configuré avant la perte d'un droit
  // (ou déplacé sur un autre bâtiment, ou sur un produit legacy qui ne les fournit pas) garderait
  // sinon des lignes qui n'afficheront rien.
  useEffect(() => {
    if (!accessRights && selectedDeviceType === undefined) return;
    setConfig((previous) => {
      const current = (previous.metrics as string[] | undefined) ?? [];
      const allowed = current.filter((metric) => {
        if (!isMetricSupported(metric, selectedDeviceType)) return false;
        const required = metricAccessRight(metric, selectedDeviceType);
        return (
          !accessRights || !required || accessRights.granted.includes(required)
        );
      });
      const chartType = previous.chartType as string | undefined;
      const chartSupported =
        chartType === undefined ||
        isMetricSupported(chartType, selectedDeviceType);
      if (allowed.length === current.length && chartSupported) return previous;

      const keptHighlights = (["primaryMetric", "secondaryMetric"] as const)
        .map((key) => previous[key] as string | undefined)
        .filter((name): name is string => Boolean(name))
        .filter((name) => allowed.includes(name));

      const next = {
        ...previous,
        metrics: allowed,
        primaryMetric: keptHighlights[0],
        secondaryMetric: keptHighlights[1],
        // Courbe sur une grandeur que le produit ne fournit pas : retour au défaut (fourni par tous).
        chartType: chartSupported ? chartType : "temperatureInside",
      };
      persistDraft(next, dimensions);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessRights, selectedDeviceType]);

  // Sélection par défaut : premier régulateur ACCESSIBLE si aucun n'est encore choisi. Le nom du
  // widget suit le produit choisi — c'est ce que l'utilisateur lit dans le bandeau.
  useEffect(() => {
    if (!entry || !size || devices.length === 0) return;
    if (config.deviceUuid !== undefined) return;
    const first = devices[0];
    const next = {
      ...config,
      deviceUuid: first.uuid,
      name: first.name || first.uuid,
    };
    setConfig(next);
    setWidgetDraft({
      widgetType: entry.id,
      sizeId: size.id,
      config: next,
      editingWidgetId: draft?.editingWidgetId,
      w: dimensions.w,
      h: dimensions.h,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, entry, size]);

  if (!entry || !size) {
    return <Navigate to="/widgets" replace />;
  }

  if (!draft?.editingWidgetId && draft?.widgetType !== entry.id) {
    return <Navigate to="/widgets" replace />;
  }

  if (!draft) {
    return <Navigate to="/widgets" replace />;
  }

  // La variante de layout (ex. RegulatorWidget) suit la taille courante.
  const matchedSize = findSizeByDimensions(entry, dimensions.w, dimensions.h);
  const previewProps = buildWidgetPropsFromConfig(entry, config, matchedSize);

  // Écrit le brouillon en conservant editingWidgetId et les dimensions courantes
  // (corrige la perte du mode édition lors d'un changement de champ).
  const persistDraft = (
    nextConfig: Record<string, unknown>,
    dims: { w: number; h: number }
  ) => {
    setWidgetDraft({
      widgetType: entry.id,
      sizeId: size.id,
      config: nextConfig,
      editingWidgetId: draft?.editingWidgetId,
      w: dims.w,
      h: dims.h,
    });
  };

  const handleResize = (w: number, h: number) => {
    setDimensions({ w, h });
    persistDraft(config, { w, h });
  };

  // Sections du configurateur, dans l'ordre d'apparition des champs. Les champs sans `group`
  // restent en tête (ex. le sélecteur de bâtiment) ; « chart » est renvoyé en dernier pour que
  // le choix de la courbe suive la sélection des grandeurs qu'elle peut tracer.
  // Grandeurs que le produit choisi ne fournit pas : pas de case (une section vide disparaît).
  const visibleFields = entry.configFields.filter(
    (field) =>
      !field.key.startsWith("metric:") ||
      isMetricSupported(field.key.slice("metric:".length), selectedDeviceType)
  );
  const otherFields = visibleFields.filter((field) => !field.group);
  const groupedFields = visibleFields.reduce<
    { group: string; fields: WidgetConfigField[] }[]
  >((sections, field) => {
    if (!field.group) return sections;
    const existing = sections.find((section) => section.group === field.group);
    if (existing) existing.fields.push(field);
    else sections.push({ group: field.group, fields: [field] });
    return sections;
  }, []);
  // Les sections de fin sont ordonnées : on choisit d'abord SES grandeurs, puis lesquelles
  // mettre en avant, puis quoi tracer — chaque étape dépend de la précédente.
  const SECTION_ORDER = ["chart"];
  groupedFields.sort(
    (a, b) =>
      (SECTION_ORDER.indexOf(a.group) + 1 || 0) -
      (SECTION_ORDER.indexOf(b.group) + 1 || 0)
  );

  const resolveOptions = (field: WidgetConfigField) => {
    if (field.dynamicOptions === "devices") {
      // Libellé : nom du régulateur, complété du type de produit (serenity / avibox / …) qui
      // détermine les grandeurs disponibles. Repli sur l'uuid quand le produit n'a pas de nom —
      // une ligne vide dans un sélecteur est inutilisable.
      return devices.map((device) => ({
        value: device.uuid,
        label: [device.name || device.uuid, device.type]
          .filter(Boolean)
          .join(" · "),
      }));
    }
    if (field.dynamicOptions === "buildings") {
      return buildings.map((building) => ({
        value: String(building.id),
        label: building.name,
      }));
    }
    if (field.dynamicOptions === "selectedMetrics") {
      // Ne proposer en vedette que les grandeurs cochées : mettre en avant une grandeur qui
      // n'est pas affichée n'aurait aucun sens.
      return ((config.metrics as string[] | undefined) ?? []).map((metric) => ({
        value: metric,
        label:
          REGULATOR_METRIC_OPTIONS.find((option) => option.value === metric)
            ?.label ?? metric,
      }));
    }
    if (field.key === "chartType") {
      return (field.options ?? []).filter((option) =>
        isMetricSupported(String(option.value), selectedDeviceType)
      );
    }
    return field.options ?? [];
  };

  // Clés `metric:<nom>` : ces cases ne sont pas des champs de config indépendants, elles
  // composent le tableau `metrics` que le widget affiche. On garde l'ORDRE de sélection pour
  // que l'utilisateur retrouve ses grandeurs dans l'ordre où il les a cochées.
  const selectedMetrics = (config.metrics as string[] | undefined) ?? [];
  const metricLimit = entry.maxIndicators;
  const limitReached =
    metricLimit !== undefined && selectedMetrics.length >= metricLimit;

  /**
   * Une grandeur est verrouillée quand son droit bigdata n'est pas accordé : BIGDATA masque
   * le bloc, donc la cocher n'afficherait jamais rien. Tant que les droits ne sont pas chargés
   * (`accessRights === null`), on ne verrouille rien.
   */
  const missingRightFor = (metricName: string) =>
    missingRight(rights, metricName, selectedDeviceType);

  /** Droit manquant commun à toutes les grandeurs d'une section, pour l'annoter au titre. */
  const missingRightForGroup = (group: string): string | undefined => {
    const metrics = REGULATOR_METRIC_OPTIONS.filter(
      (option) =>
        option.group === group &&
        isMetricSupported(option.value, selectedDeviceType)
    );
    if (metrics.length === 0) return undefined;
    const missing = metrics.map((option) => missingRightFor(option.value));
    // Annotation seulement si TOUTE la section est verrouillée (le cheptel mélange
    // mortality/weighing/aucun droit, donc n'est jamais annoté en bloc).
    return missing.every((right) => right !== undefined)
      ? missing[0]
      : undefined;
  };

  const metricNameOf = (key: string) =>
    key.startsWith("metric:") ? key.slice("metric:".length) : undefined;

  // Vedettes : jusqu'à 2 grandeurs mises en avant, stockées dans `primaryMetric` /
  // `secondaryMetric` (le widget attend ces deux clés). On les manipule comme une liste
  // ordonnée : la 1re promue devient la principale.
  const highlightKeys = ["primaryMetric", "secondaryMetric"] as const;
  const highlighted = highlightKeys
    .map((key) => config[key] as string | undefined)
    .filter((metric): metric is string => Boolean(metric));

  const toggleHighlight = (metricName: string) => {
    setConfig((previous) => {
      const current = highlightKeys
        .map((key) => previous[key] as string | undefined)
        .filter((metric): metric is string => Boolean(metric));
      const next = current.includes(metricName)
        ? current.filter((name) => name !== metricName)
        : current.length >= highlightKeys.length
          ? current // 2 vedettes déjà prises : le bouton est désactivé, garde-fou seulement
          : [...current, metricName];

      const updated = {
        ...previous,
        primaryMetric: next[0],
        secondaryMetric: next[1],
      };
      persistDraft(updated, dimensions);
      return updated;
    });
  };

  const updateField = (field: WidgetConfigField, rawValue: unknown) => {
    const metricName = metricNameOf(field.key);
    if (metricName) {
      setConfig((previous) => {
        const current = (previous.metrics as string[] | undefined) ?? [];
        const isSelected = current.includes(metricName);
        // Au-delà de la limite, cocher une case de plus est refusé silencieusement (la tuile
        // est déjà désactivée visuellement) ; décocher reste toujours possible.
        if (
          !isSelected &&
          metricLimit !== undefined &&
          current.length >= metricLimit
        ) {
          return previous;
        }
        const metrics = isSelected
          ? current.filter((name) => name !== metricName)
          : [...current, metricName];
        const next: Record<string, unknown> = { ...previous, metrics };

        // Décocher une grandeur la retire aussi de la mise en avant, sans la remplacer :
        // c'est l'utilisateur qui choisit ses vedettes à la punaise, on ne décide pas pour lui.
        // On recompacte pour que `secondaryMetric` ne reste jamais seule renseignée.
        const keptHighlights = highlightKeys
          .map((key) => previous[key] as string | undefined)
          .filter((name): name is string => Boolean(name))
          .filter((name) => name !== metricName && metrics.includes(name));
        next.primaryMetric = keptHighlights[0];
        next.secondaryMetric = keptHighlights[1];

        persistDraft(next, dimensions);
        return next;
      });
      return;
    }

    setConfig((previous) => {
      let next: Record<string, unknown>;

      if (field.type === "citySearch") {
        // La recherche de ville fixe la localisation (lat/lon) + le libellé.
        const geo = rawValue as GeoResult;
        next = {
          ...previous,
          latitude: geo.latitude,
          longitude: geo.longitude,
          locationName: geo.name,
        };
      } else {
        let value = rawValue;
        if (field.valueType === "number" && typeof rawValue === "string") {
          value = rawValue === "" ? undefined : Number(rawValue);
        }

        next = { ...previous, [field.key]: value };

        // Le sélecteur de bâtiment conserve aussi le libellé (display.name).
        if (field.dynamicOptions === "buildings") {
          const building = buildings.find((b) => b.id === value);
          if (building) next.name = building.name;
        }

        // Idem pour le régulateur : le titre du widget suit le produit choisi. Repli sur l'uuid,
        // sans quoi un produit sans nom laisserait le titre du widget précédent.
        if (field.dynamicOptions === "devices") {
          const device = devices.find((item) => item.uuid === value);
          if (device) next.name = device.name || device.uuid;
        }
      }

      persistDraft(next, dimensions);
      return next;
    });
  };

  // Rend un groupe : cases à cocher en tuiles (grille), autres champs en dessous.
  const renderGroupFields = (fields: WidgetConfigField[]) => {
    const tiles = fields.filter((field) => field.type === "checkbox");
    const others = fields.filter((field) => field.type !== "checkbox");
    return (
      <div className="space-y-3">
        {tiles.length > 0 && (
          <div className="grid grid-cols-2 gap-2">
            {tiles.map((field) => {
              const metricName = metricNameOf(field.key);
              // Une grandeur est active si elle est dans `metrics` ; un champ classique garde
              // son booléen propre.
              const active = metricName
                ? selectedMetrics.includes(metricName)
                : Boolean(config[field.key]);
              return (
                <IndicatorTile
                  key={field.key}
                  label={field.label}
                  icon={field.icon}
                  active={active}
                  // Verrouillée si le droit manque, ou si la limite de 8 est atteinte.
                  // Le droit prime : son message est plus informatif.
                  disabled={
                    Boolean(metricName && missingRightFor(metricName)) ||
                    (Boolean(metricName) && !active && limitReached)
                  }
                  lockedReason={
                    metricName && missingRightFor(metricName)
                      ? t("regulator-right-not-granted", {
                          right:
                            ACCESS_RIGHT_LABELS[missingRightFor(metricName)!] ??
                            missingRightFor(metricName),
                        })
                      : undefined
                  }
                  onToggle={() => updateField(field, !active)}
                  color={metricName ? METRIC_COLORS[metricName] : undefined}
                  highlighted={
                    metricName ? highlighted.includes(metricName) : false
                  }
                  highlightDisabled={
                    metricName
                      ? !highlighted.includes(metricName) &&
                        highlighted.length >= highlightKeys.length
                      : false
                  }
                  // La punaise n'est proposée que sur les grandeurs (pas sur showChart & co.).
                  onToggleHighlight={
                    metricName ? () => toggleHighlight(metricName) : undefined
                  }
                />
              );
            })}
          </div>
        )}
        {others.map((field) => (
          <ConfigField
            key={field.key}
            field={field}
            value={config[field.key]}
            options={resolveOptions(field)}
            onChange={(value) => updateField(field, value)}
          />
        ))}
      </div>
    );
  };

  const isEditingExisting = Boolean(draft?.editingWidgetId);

  const handleCancel = () => {
    clearWidgetDraft();
    navigate(isEditingExisting ? "/" : "/widgets");
  };

  const handleAdd = () => {
    if (draft?.editingWidgetId) {
      setPendingWidgetUpdate({
        widgetId: draft.editingWidgetId,
        widgetType: entry.id,
        props: previewProps,
        w: dimensions.w,
        h: dimensions.h,
      });
    } else {
      setPendingWidget({
        widgetType: entry.id,
        props: previewProps,
        w: dimensions.w,
        h: dimensions.h,
      });
    }
    clearWidgetDraft();
    navigate("/");
  };

  return (
    <div className="p-4 min-h-screen">
      <Header
        title={`Widget ${entry.label}`}
        icon={sectionToIcon.section_dashboard}
      />
      <p className="text-base text-grey-readable mt-4 mb-8 max-w-3xl">
        {entry.description} Redimensionnez l'aperçu (poignée en bas à droite)
        pour définir la taille exacte du widget sur le dashboard.
      </p>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 items-start">
        <div className="flex justify-center xl:justify-start overflow-auto">
          <ResizableWidgetPreview
            widgetType={entry.id}
            props={previewProps}
            w={dimensions.w}
            h={dimensions.h}
            onResize={handleResize}
            minW={entry.minW}
            minH={entry.minH}
          />
        </div>

        <div className="bg-white rounded-xl border border-grey-light shadow-sm p-6 space-y-6">
          {otherFields.map((field) => (
            <ConfigField
              key={field.key}
              field={field}
              value={config[field.key]}
              options={resolveOptions(field)}
              onChange={(value) => updateField(field, value)}
            />
          ))}

          {hasDeviceField && devicesError && (
            <p className="text-sm text-warning-readable flex items-center gap-2">
              {t("regulator-list-unavailable")}
              <button
                type="button"
                onClick={() => setDevicesAttempt((n) => n + 1)}
                className="text-tertiary underline hover:no-underline"
              >
                {t("regulator-retry")}
              </button>
            </p>
          )}
          {hasDeviceField &&
            devicesLoaded &&
            !devicesError &&
            devices.length === 0 && (
              <p className="text-sm text-grey-readable">
                {t("regulator-none-available")}
              </p>
            )}
          {rightsNotice && (
            <p className="text-sm text-warning-readable">{rightsNotice}</p>
          )}

          {/* Compteur + mode d'emploi de la punaise : la limite doit être visible AVANT de buter
              dessus, et la mise en avant n'est pas découvrable sans un mot d'explication. */}
          {metricLimit !== undefined && (
            <div className="space-y-1">
              <p
                className={`text-sm ${
                  limitReached ? "text-tertiary" : "text-grey-readable"
                }`}
              >
                {selectedMetrics.length} / {metricLimit} grandeurs sélectionnées
                {limitReached && " — décochez-en une pour en choisir une autre"}
              </p>
              <p className="text-sm text-grey-readable flex items-center gap-1.5">
                <FontAwesomeIcon icon="thumbtack" className="text-xs" />
                Cliquez sur la punaise d'une grandeur pour la mettre en avant
                dans le widget ({highlighted.length} / {highlightKeys.length})
              </p>
            </div>
          )}

          {groupedFields.map(({ group, fields }) => {
            const missingRight = missingRightForGroup(group);
            return (
              <div key={group}>
                <p className="text-tertiary mb-3 font-oxaniumMedium flex items-center gap-2 flex-wrap">
                  {GROUP_LABELS[group] ?? group}
                  {/* Section entièrement verrouillée : on le dit au titre plutôt que de
                      laisser l'utilisateur cliquer sur chaque tuile pour comprendre. */}
                  {missingRight && (
                    <span
                      className="text-sm font-normal text-warning-readable bg-warning/10 px-2 py-0.5 rounded-full inline-flex items-center gap-1.5"
                      title={t("regulator-right-not-granted", {
                        right:
                          ACCESS_RIGHT_LABELS[missingRight] ?? missingRight,
                      })}
                    >
                      <FontAwesomeIcon icon="lock" className="text-xs" />
                      Accès non autorisé
                    </span>
                  )}
                </p>
                {renderGroupFields(fields)}
              </div>
            );
          })}

          {entry.configFields.length === 0 && (
            <p className="text-base text-grey-readable">
              Aucun paramètre supplémentaire pour ce widget. Cliquez sur Ajouter
              pour le placer sur le dashboard.
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              variant="secondary"
              size="full"
              onClick={handleCancel}
              className="normal-case tracking-normal"
            >
              Annuler
            </Button>
            <Button size="full" onClick={handleAdd}>
              {isEditingExisting ? "Enregistrer" : "Ajouter"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
